/**
 * Central path constants so a route is never hand-typed as a string literal in more than one
 * place. Persona feature route tables extend `ROOT` with their own sub-trees.
 */
export const ROUTES = {
  root: '/',
  login: '/login',
  register: '/register',
  registerCompany: '/register-company',
  registerCompanyVerify: '/register-company/verify',
  verifyEmail: '/verify-email',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  mfaChallenge: '/mfa',
  forgotMfa: '/forgot-mfa',
  sessionExpired: '/session-expired',
  unauthorized: '/unauthorized',
  forbidden: '/403',
  notFound: '/404',
  profile: '/account/profile',
  changePassword: '/account/change-password',
  mfaSetup: '/account/mfa-setup',
  notificationSettings: '/account/notifications',
  notificationsHistory: '/account/notifications/all',
  /** Discovered company invitations addressed to the signed-in user's verified email. */
  accountInvitations: '/account/invitations',
  /** Context-aware onboarding — the neutral "choose your first business journey" screen. */
  welcome: '/welcome',
  /** Workspace chooser for accounts holding both a Candidate profile and Company membership(s). */
  workspaces: '/workspaces',
  /** Anonymous invitation landing page — the target of the invitation email's accept link. */
  invitationAccept: '/invitations/accept',
  /**
   * The active legal documents (ADR-0026). Public and outside every auth gate — the registration form
   * links here before an account exists, and these are also the addresses to cite when someone asks
   * what they agreed to.
   */
  legalTerms: '/legal/terms',
  legalPrivacy: '/legal/privacy',
  /** The index of every published document. Linked from the footer and from each document page. */
  legalIndex: '/legal',
  /** Public, anonymous job board (Candidate Portal). Reachable signed-in or not. */
  jobs: '/jobs',
  /**
   * Public, anonymous employer directory (ADR-0023 §7) — the peer of the job board. `/companies/{slug}` and
   * `/companies/{slug}/jobs` hang off it, and the profile is the canonical shareable address of an employer:
   * it is what invitation notifications link to, so it must resolve for a visitor with no account.
   */
  companies: '/companies',
  candidate: '/candidate',
  recruiter: '/recruiter',
  company: '/company',
  vendor: '/vendor',
  admin: '/admin',
} as const;

export type AppRoute = (typeof ROUTES)[keyof typeof ROUTES];

/**
 * The address of any published legal document, e.g. `/legal/cookie-policy`.
 *
 * A standalone function rather than a `ROUTES` entry, deliberately: `AppRoute` is derived from the
 * VALUES of `ROUTES`, so a function member would widen that exported type from "a route path" to "a
 * route path or a function", quietly defeating it for every consumer.
 *
 * The slug is whatever the API returned on the document (`LegalDocument.slug`) — never assembled from
 * a list held here, so a newly published document type is linkable without a frontend release.
 * `ROUTES.legalTerms` and `ROUTES.legalPrivacy` remain their own fixed addresses: they are in the wild,
 * in emails and in the registration consent checkbox, and must not start 404ing.
 */
export const legalDocumentRoute = (slug: string) => `${ROUTES.legalIndex}/${slug}`;
